import { fieldNames } from "../hooks/useLicenseSearch";
import { calcLicenseFee, formatFee } from "../utils/licenseFee";
import styles from "../styles/_components/licenseModal.module.scss";
import { format } from "date-fns";
import { useEffect, useState, useRef } from "react";

const style = {
  height: "100vh",
  width: "100%",
  position: "fixed",
  left: "0px",
  top: "0px",
  opacity: 0.45,
  background: "#000000 0% 0% no-repeat padding-box",
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return format(date, "dd/MM/yyyy");
};

const Backdrop = ({ setIsOpen }) => (
  <div style={style} onClick={() => setIsOpen(null)}></div>
);

// Utility function to remove leading zeros
const removeLeadingZeros = (value) => {
  if (typeof value === "string") {
    return value.replace(/^0+/, "");
  }
  return value;
};

// Add helper function for gearbox type
const getGearboxType = (value) => {
  const gearTypes = {
    0: "ידני",
    1: "אוטומט",
    2: "רובוטי",
    3: "טיפטרוניק",
  };
  return gearTypes[value] || value;
};

// Field labels for registration/history dataset (resource 56063a99-...)
const historyFieldNames = {
  mispar_rechev: "מספר רישוי",
  mispar_manoa: "מספר מנוע",
  kilometer_test_aharon: "קילומטר בטסט אחרון",
  shinui_mivne_ind: "שינוי מבנה",
  gapam_ind: "האם גפ״מ",
  shnui_zeva_ind: "שינוי צבע",
  shinui_zmig_ind: "שינוי צמיג",
  rishum_rishon_dt: "תאריך רישום ראשון",
  mkoriut_nm: "במקור",
};

const toYesNo = (value) => (Number(value) === 1 ? "כן" : "לא");

// Field labels for recalls dataset (resource 36bf1404-...)
const recallFieldNames = {
  RECALL_ID: "מזהה ריקול",
  SUG_RECALL: "סוג ריקול",
  SUG_TAKALA: "סוג תקלה",
  TEUR_TAKALA: "תיאור תקלה",
  TAARICH_PTICHA: "תאריך פתיחה",
};

const LicenseModal = ({ setIsOpen, licenseData, onHydratedData }) => {
  const cacheRef = useRef({});
  const [stableData, setStableData] = useState(licenseData || {});
  const normalizePlate = (v) => String(v || "").replace(/\D/g, "");

  // Modal states
  const [hasDisabledTag, setHasDisabledTag] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [recalls, setRecalls] = useState(null);
  const [isRecallsLoading, setIsRecallsLoading] = useState(false);
  const [showRecallDetails, setShowRecallDetails] = useState(false);
  const [isVehicleInactiveA, setIsVehicleInactiveA] = useState(false);
  const [isVehicleInactiveB, setIsVehicleInactiveB] = useState(false);
  const [isVehicleInactiveC, setIsVehicleInactiveC] = useState(false);
  const [isVehicleInactiveD, setIsVehicleInactiveD] = useState(false);
  const [bitulDt, setBitulDt] = useState(null);
  const [priceILS, setPriceILS] = useState(null);
  const [isPriceLoading, setIsPriceLoading] = useState(false);

  // Persist best-known details per plate and render from them
  useEffect(() => {
    try {
      const plate = normalizePlate(licenseData?.mispar_rechev);
      let merged = licenseData || {};
      if (plate) {
        const cached = cacheRef.current[plate] || {};
        merged = { ...cached, ...licenseData };
        cacheRef.current[plate] = merged;
      }
      setStableData(merged);
      try {
        if (typeof onHydratedData === "function") onHydratedData(merged);
      } catch {}
    } catch {}
  }, [licenseData]);

  // Debug: log safety accessories data as it arrives
  useEffect(() => {
    try {
      if (!licenseData) return;
      const safetyFields = [
        "nikud_betihut",
        "sug_tkina_nm",
        "mispar_kariot_avir",
        "bakarat_yatzivut_ind",
        "bakarat_stiya_menativ_ind",
        "nitur_merhak_milfanim_ind",
        "bakarat_shyut_adaptivit_ind",
        "zihuy_beshetah_nistar_ind",
        "zihuy_holchey_regel_ind",
        "maarechet_ezer_labalam_ind",
        "matzlemat_reverse_ind",
        "hayshaney_lahatz_avir_batzmigim_ind",
        "hayshaney_hagorot_ind",
        "abs_ind",
        "teura_automatit_benesiya_kadima_ind",
        "shlita_automatit_beorot_gvohim_ind",
        "zihuy_matzav_hitkarvut_mesukenet_ind",
        "zihuy_tamrurey_tnua_ind",
      ];
      const payload = safetyFields.reduce((acc, key) => {
        if (licenseData[key] !== undefined) acc[key] = licenseData[key];
        return acc;
      }, {});
      // Single consolidated log for the whole section
      console.log("SAFETY_ACCESSORIES_DATA", payload);
    } catch (e) {
      // no-op
    }
  }, [licenseData]);

  // Debug: log basic vehicle details snapshot
  useEffect(() => {
    try {
      if (!licenseData) return;
      const fields = [
        "mispar_rechev",
        "tozeret_nm",
        "kinuy_mishari",
        "shnat_yitzur",
        "tzeva_rechev",
        "misgeret",
        "baalut",
        "merkav",
      ];
      const payload = fields.reduce((acc, key) => {
        if (licenseData[key] !== undefined) acc[key] = licenseData[key];
        return acc;
      }, {});
      console.log("BASIC_VEHICLE_DETAILS", payload);
    } catch {}
  }, [licenseData]);

  // Query data.gov.il to check disabled tag ("תו נכה") by plate number
  useEffect(() => {
    const plateRaw = licenseData?.mispar_rechev
      ? String(licenseData.mispar_rechev)
      : null;
    if (!plateRaw) {
      setHasDisabledTag(null);
      return;
    }

    const numericPlate = parseInt(plateRaw.replace(/\D/g, ""), 10);
    if (!Number.isFinite(numericPlate)) {
      setHasDisabledTag(false);
      return;
    }

    let isCanceled = false;
    setHasDisabledTag(null);

    const filters = encodeURIComponent(
      JSON.stringify({ "MISPAR RECHEV": numericPlate })
    );
    const url = `https://data.gov.il/api/3/action/datastore_search?resource_id=c8b9f9c8-4612-4068-934f-d4acd2e3c06e&limit=5&filters=${filters}`;

    fetch(url, { method: "GET", cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        if (isCanceled) return;
        const records = data?.result?.records || [];
        const has = records.some((r) => Number(r["SUG TAV"]) === 1);
        setHasDisabledTag(Boolean(has));
      })
      .catch(() => {
        if (isCanceled) return;
        setHasDisabledTag(false);
      });

    return () => {
      isCanceled = true;
    };
  }, [licenseData?.mispar_rechev]);

  // Check deregistered vehicles dataset D (851ecab1) by plate number
  useEffect(() => {
    const plateRaw = licenseData?.mispar_rechev
      ? String(licenseData.mispar_rechev)
      : null;
    if (!plateRaw) {
      setIsVehicleInactiveD(false);
      return;
    }
    const numericPlate = parseInt(plateRaw.replace(/\D/g, ""), 10);
    if (!Number.isFinite(numericPlate)) {
      setIsVehicleInactiveD(false);
      return;
    }
    let isCanceled = false;
    setIsVehicleInactiveD(false);
    const filters = encodeURIComponent(
      JSON.stringify({ mispar_rechev: numericPlate })
    );
    const url = `https://data.gov.il/api/3/action/datastore_search?resource_id=851ecab1-0622-4dbe-a6c7-f950cf82abf9&limit=1&filters=${filters}`;
    fetch(url, { method: "GET", cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        if (isCanceled) return;
        const records = data?.result?.records || [];
        const hasAny = Array.isArray(records) && records.length > 0;
        setIsVehicleInactiveD(hasAny);
        if (hasAny && !bitulDt) {
          const first = records[0];
          const bd = first ? first["bitul_dt"] ?? null : null;
          if (bd) setBitulDt(bd);
        }
      })
      .catch(() => {
        if (isCanceled) return;
        setIsVehicleInactiveD(false);
      });
    return () => {
      isCanceled = true;
    };
  }, [licenseData?.mispar_rechev]);

  // Fetch indicative price (39f455bf) by best-effort matching of available fields
  useEffect(() => {
    const tozeretCd = licenseData?.tozeret_cd
      ? Number(String(licenseData.tozeret_cd).replace(/\D/g, ""))
      : null;
    const degemCd = licenseData?.degem_cd
      ? Number(String(licenseData.degem_cd).replace(/\D/g, ""))
      : null;
    const tozeretNm = licenseData?.tozeret_nm || null;
    const degemNm = licenseData?.degem_nm || null;
    const sugDegem = licenseData?.sug_degem || null;
    const year = licenseData?.shnat_yitzur
      ? Number(String(licenseData.shnat_yitzur).replace(/\D/g, ""))
      : licenseData?.yop
      ? Number(String(licenseData.yop).replace(/\D/g, ""))
      : null;

    // If nothing meaningful to query, reset and bail
    if (!tozeretCd && !tozeretNm && !degemCd && !degemNm && !year) {
      setPriceILS(null);
      setIsPriceLoading(false);
      return;
    }

    let isCanceled = false;
    setPriceILS(null);
    setIsPriceLoading(true);

    const attempts = [];
    // Order: stricter to looser
    if (tozeretCd && degemCd && year && sugDegem)
      attempts.push({
        tozeret_cd: tozeretCd,
        degem_cd: degemCd,
        shnat_yitzur: year,
        sug_degem: sugDegem,
      });
    if (tozeretCd && degemCd && year)
      attempts.push({
        tozeret_cd: tozeretCd,
        degem_cd: degemCd,
        shnat_yitzur: year,
      });
    if (tozeretCd && degemNm && year)
      attempts.push({
        tozeret_cd: tozeretCd,
        degem_nm: degemNm,
        shnat_yitzur: year,
      });
    if (tozeretNm && degemCd && year)
      attempts.push({
        tozeret_nm: tozeretNm,
        degem_cd: degemCd,
        shnat_yitzur: year,
      });
    if (tozeretNm && degemNm && year)
      attempts.push({
        tozeret_nm: tozeretNm,
        degem_nm: degemNm,
        shnat_yitzur: year,
      });
    if (degemNm && year)
      attempts.push({ degem_nm: degemNm, shnat_yitzur: year });
    if (tozeretCd && degemCd)
      attempts.push({ tozeret_cd: tozeretCd, degem_cd: degemCd });
    if (tozeretNm && degemNm)
      attempts.push({ tozeret_nm: tozeretNm, degem_nm: degemNm });

    const pickRecord = (records) => {
      if (!Array.isArray(records) || records.length === 0) return null;
      // Prefer exact degem_nm match if we have it
      if (degemNm) {
        const target = String(degemNm).trim().toLowerCase();
        const exact = records.find(
          (r) => String(r["degem_nm"]).trim().toLowerCase() === target
        );
        if (exact) return exact;
      }
      return records[0];
    };

    (async () => {
      for (const filt of attempts) {
        try {
          const filters = encodeURIComponent(JSON.stringify(filt));
          const url = `https://data.gov.il/api/3/action/datastore_search?resource_id=39f455bf-6db0-4926-859d-017f34eacbcb&limit=50&filters=${filters}`;
          const res = await fetch(url, { method: "GET", cache: "no-store" });
          const data = await res.json();
          if (isCanceled) return;
          const records = data?.result?.records || [];
          const rec = pickRecord(records);
          if (rec && rec["mehir"] != null) {
            setPriceILS(Number(rec["mehir"]));
            break;
          }
        } catch (e) {
          if (isCanceled) return;
          // continue to next attempt
        }
      }
      if (!isCanceled) setIsPriceLoading(false);
    })();

    return () => {
      isCanceled = true;
    };
  }, [
    licenseData?.tozeret_cd,
    licenseData?.tozeret_nm,
    licenseData?.degem_cd,
    licenseData?.degem_nm,
    licenseData?.sug_degem,
    licenseData?.shnat_yitzur,
    licenseData?.yop,
  ]);

  // Fetch registration/history data by plate number
  useEffect(() => {
    const plateRaw = licenseData?.mispar_rechev
      ? String(licenseData.mispar_rechev)
      : null;
    if (!plateRaw) {
      setHistoryData(null);
      return;
    }

    const numericPlate = parseInt(plateRaw.replace(/\D/g, ""), 10);
    if (!Number.isFinite(numericPlate)) {
      setHistoryData(null);
      return;
    }

    let isCanceled = false;
    setIsHistoryLoading(true);
    setHistoryData(null);

    const filters = encodeURIComponent(
      JSON.stringify({ mispar_rechev: numericPlate })
    );
    const url = `https://data.gov.il/api/3/action/datastore_search?resource_id=56063a99-8a3e-4ff4-912e-5966c0279bad&limit=1&filters=${filters}`;

    fetch(url, { method: "GET", cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        if (isCanceled) return;
        const records = data?.result?.records || [];
        setHistoryData(records[0] || null);
      })
      .catch(() => {
        if (isCanceled) return;
        setHistoryData(null);
      })
      .finally(() => {
        if (isCanceled) return;
        setIsHistoryLoading(false);
      });

    return () => {
      isCanceled = true;
    };
  }, [licenseData?.mispar_rechev]);

  // Fetch recall records by plate number (open recalls assumed as listed)
  useEffect(() => {
    const plateRaw = licenseData?.mispar_rechev
      ? String(licenseData.mispar_rechev)
      : null;
    if (!plateRaw) {
      setRecalls(null);
      setShowRecallDetails(false);
      return;
    }

    const numericPlate = parseInt(plateRaw.replace(/\D/g, ""), 10);
    if (!Number.isFinite(numericPlate)) {
      setRecalls([]);
      setShowRecallDetails(false);
      return;
    }

    let isCanceled = false;
    setIsRecallsLoading(true);
    setRecalls(null);
    setShowRecallDetails(false);

    const filters = encodeURIComponent(
      JSON.stringify({ MISPAR_RECHEV: numericPlate })
    );
    const url = `https://data.gov.il/api/3/action/datastore_search?resource_id=36bf1404-0be4-49d2-82dc-2f1ead4a8b93&limit=100&filters=${filters}`;

    fetch(url, { method: "GET", cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        if (isCanceled) return;
        const records = data?.result?.records || [];
        setRecalls(records);
      })
      .catch(() => {
        if (isCanceled) return;
        setRecalls([]);
      })
      .finally(() => {
        if (isCanceled) return;
        setIsRecallsLoading(false);
      });

    return () => {
      isCanceled = true;
    };
  }, [licenseData?.mispar_rechev]);

  // Check inactive vehicle dataset A (f6efe89a) by plate number
  useEffect(() => {
    const plateRaw = licenseData?.mispar_rechev
      ? String(licenseData.mispar_rechev)
      : null;
    if (!plateRaw) {
      setIsVehicleInactiveA(false);
      setBitulDt(null);
      setIsVehicleInactiveC(false);
      return;
    }

    const numericPlate = parseInt(plateRaw.replace(/\D/g, ""), 10);
    if (!Number.isFinite(numericPlate)) {
      setIsVehicleInactiveA(false);
      setBitulDt(null);
      setIsVehicleInactiveC(false);
      return;
    }

    let isCanceled = false;
    setIsVehicleInactiveA(false);
    setBitulDt(null);
    setIsVehicleInactiveC(false);

    const filters = encodeURIComponent(
      JSON.stringify({ mispar_rechev: numericPlate })
    );
    const url = `https://data.gov.il/api/3/action/datastore_search?resource_id=f6efe89a-fb3d-43a4-bb61-9bf12a9b9099&limit=1&filters=${filters}`;

    fetch(url, { method: "GET", cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        if (isCanceled) return;
        const records = data?.result?.records || [];
        const hasAny = Array.isArray(records) && records.length > 0;
        setIsVehicleInactiveA(hasAny);
        const first = hasAny ? records[0] : null;
        const rawBitul = first ? first["bitul_dt"] ?? null : null;
        setBitulDt(rawBitul || null);

        // Fallback: if not found in f6efe89a, try 4e6b9724 (string IDs with leading zeros)
        if (!hasAny) {
          const padded = String(numericPlate).padStart(8, "0");
          const fallbackFilters = encodeURIComponent(
            JSON.stringify({ mispar_rechev: padded })
          );
          const fallbackUrl = `https://data.gov.il/api/3/action/datastore_search?resource_id=4e6b9724-4c1e-43f0-909a-154d4cc4e046&limit=1&filters=${fallbackFilters}`;

          fetch(fallbackUrl, { method: "GET", cache: "no-store" })
            .then((res) => res.json())
            .then((fbData) => {
              if (isCanceled) return;
              const fbRecords = fbData?.result?.records || [];
              const fbHasAny = Array.isArray(fbRecords) && fbRecords.length > 0;
              setIsVehicleInactiveC(fbHasAny);
              const fbFirst = fbHasAny ? fbRecords[0] : null;
              const fbBitul = fbFirst ? fbFirst["bitul_dt"] ?? null : null;
              if (!rawBitul && fbBitul) {
                setBitulDt(fbBitul);
              }
            })
            .catch(() => {
              if (isCanceled) return;
              setIsVehicleInactiveC(false);
            });
        }
      })
      .catch(() => {
        if (isCanceled) return;
        setIsVehicleInactiveA(false);
        setBitulDt(null);
        setIsVehicleInactiveC(false);
      });

    return () => {
      isCanceled = true;
    };
  }, [licenseData?.mispar_rechev]);

  // Check inactive vehicle dataset B (6f6acd03) by plate number
  useEffect(() => {
    const plateRaw = licenseData?.mispar_rechev
      ? String(licenseData.mispar_rechev)
      : null;
    if (!plateRaw) {
      setIsVehicleInactiveB(false);
      return;
    }

    const numericPlate = parseInt(plateRaw.replace(/\D/g, ""), 10);
    if (!Number.isFinite(numericPlate)) {
      setIsVehicleInactiveB(false);
      return;
    }

    let isCanceled = false;
    setIsVehicleInactiveB(false);

    const filters = encodeURIComponent(
      JSON.stringify({ mispar_rechev: numericPlate })
    );
    const url = `https://data.gov.il/api/3/action/datastore_search?resource_id=6f6acd03-f351-4a8f-8ecf-df792f4f573a&limit=1&filters=${filters}`;

    fetch(url, { method: "GET", cache: "no-store" })
      .then((res) => res.json())
      .then((data) => {
        if (isCanceled) return;
        const records = data?.result?.records || [];
        setIsVehicleInactiveB(Array.isArray(records) && records.length > 0);
      })
      .catch(() => {
        if (isCanceled) return;
        setIsVehicleInactiveB(false);
      });

    return () => {
      isCanceled = true;
    };
  }, [licenseData?.mispar_rechev]);

  const handlePrint = () => {
    const printableContent = document.getElementById("printableContent");

    if (!printableContent) {
      console.warn("Printable content not found.");
      return;
    }

    const html = `<!doctype html>
<html lang="he" dir="rtl">
  <head>
    <meta charset="utf-8" />
    <title>Print</title>
    <style>
      @page { size: A4; margin: 10mm; }
      html, body { margin: 0; padding: 0; height: auto !important; }
      body { font-size: 12px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans Hebrew", "Noto Sans", sans-serif; color: #111; }
      .printable-root { width: 100%; }
      /* Header */
      .header h2 { margin: 0 0 8px; font-size: 18px; }
      .banner { background: #ffebee; border: 1px solid #f44336; color: #b71c1c; padding: 8px 10px; border-radius: 6px; margin: 6px 0 10px; page-break-inside: avoid; }
      /* Main details */
      .mainDetails { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px 12px; }
      .mainDetail { display: grid; grid-template-columns: 110px 1fr; align-items: center; }
      .label { font-weight: 600; }
      .value { text-align: start; }
      /* Sections */
      .sections { margin-top: 10px; }
      .section { margin: 8px 0 12px; page-break-inside: avoid; }
      .section h3 { margin: 0 0 6px; font-size: 14px; }
      ul { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px 12px; }
      li { display: grid; grid-template-columns: 1fr 1fr; align-items: center; page-break-inside: avoid; }
      li p { margin: 0; }
      /* Compact bars */
      .score, .pollution { display: flex; gap: 2px; }
      .score > div, .pollution > div { width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; font-size: 9px; color: #fff; }
      /* Hide non-essential interactive elements */
      .printBtn, .close { display: none !important; }
    </style>
  </head>
  <body>
    <div class="printable-root">
      ${printableContent.innerHTML}
    </div>
  </body>
</html>`;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    // Give the browser a tick to layout before printing to avoid blank first page
    setTimeout(() => {
      try {
        printWindow.print();
      } catch {}
      try {
        printWindow.close();
      } catch {}
    }, 250);
  };

  // console.log(licenseData, "============= LICENSE MODAL DATA ===============");

  return (
    <div className={styles.mainContainer}>
      <Backdrop setIsOpen={setIsOpen} />
      <div className={styles.container}>
        <span className={styles.close} onClick={() => setIsOpen(null)}>
          x
        </span>
        <button className={styles.printBtn} onClick={handlePrint}>
          הדפסת נתונים
        </button>
        <div className={styles.content} id="printableContent">
          <div className={styles.header}>
            <h2>נתוני הרכב</h2>
            {(isVehicleInactiveA ||
              isVehicleInactiveB ||
              isVehicleInactiveC ||
              isVehicleInactiveD) && (
              <div
                style={{
                  backgroundColor: "#ffebee",
                  border: "1px solid #f44336",
                  color: "#b71c1c",
                  padding: "10px 12px",
                  borderRadius: 6,
                  margin: "8px 0 12px",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                שימו לב: על פי נתוני משרד הרישוי רכב זה לא פעיל
              </div>
            )}
            <div className={styles.mainDetails}>
              {licenseData.manufacturer && (
                <div className={styles.mainDetail}>
                  <span className={styles.label}>יצרן</span>
                  <span className={styles.value}>
                    {licenseData.manufacturer}
                  </span>
                </div>
              )}
              {licenseData.degem_nm && (
                <div className={styles.mainDetail}>
                  <span className={styles.label}>דגם</span>
                  <span className={styles.value}>{licenseData.degem_nm}</span>
                </div>
              )}
              {licenseData.yop && (
                <div className={styles.mainDetail}>
                  <span className={styles.label}>שנת יצור</span>
                  <span className={styles.value}>
                    {removeLeadingZeros(licenseData.yop)}
                  </span>
                </div>
              )}
              {(isPriceLoading || priceILS != null) && (
                <div className={styles.mainDetail}>
                  <span className={styles.label}>מחירון (מוערך)</span>
                  <span className={styles.value}>
                    {isPriceLoading
                      ? "בודק..."
                      : new Intl.NumberFormat("he-IL", {
                          style: "currency",
                          currency: "ILS",
                          maximumFractionDigits: 0,
                        }).format(priceILS)}
                  </span>
                </div>
              )}
              {bitulDt && (
                <div className={styles.mainDetail}>
                  <span className={styles.label}>תאריך ירידה מהכביש</span>
                  <span className={styles.value}>{formatDate(bitulDt)}</span>
                </div>
              )}
              {licenseData.mispar_rechev && (
                <div className={styles.mainDetail}>
                  <span className={styles.label}>תו נכה</span>
                  <span className={styles.value}>
                    {hasDisabledTag === null
                      ? "בודק..."
                      : hasDisabledTag
                      ? "כן"
                      : "לא"}
                  </span>
                </div>
              )}
              {licenseData.mispar_rechev && (
                <div className={styles.mainDetail}>
                  <span className={styles.label}>קריאת ריקול שלא בוצעה</span>
                  <span className={styles.value}>
                    {isRecallsLoading
                      ? "בודק..."
                      : Array.isArray(recalls) && recalls.length > 0
                      ? "כן"
                      : "לא"}
                  </span>
                  {Array.isArray(recalls) &&
                    recalls.length > 0 &&
                    !isRecallsLoading && (
                      <button
                        onClick={() => setShowRecallDetails((v) => !v)}
                        style={{
                          marginInlineStart: 8,
                          padding: "2px 8px",
                          fontSize: 12,
                        }}
                      >
                        {showRecallDetails ? "הסתר" : "פרטים"}
                      </button>
                    )}
                </div>
              )}
            </div>
          </div>

          <div className={styles.sections}>
            <div className={styles.section}>
              <h3>פרטי רכב בסיסיים</h3>
              <ul>
                {[
                  "mispar_rechev",
                  "tozeret_nm",
                  "kinuy_mishari",
                  "shnat_yitzur",
                  "tzeva_rechev",
                  "misgeret",
                  "mispar_shilda",
                  "tozeret_eretz_nm",
                  "kvutzat_sug_rechev",
                  "tkina_EU",
                  "baalut",
                  "merkav",
                ].map(
                  (field) =>
                    stableData[field] && (
                      <li key={field}>
                        <p>{fieldNames[field]}:</p>
                        <p>{removeLeadingZeros(stableData[field])}</p>
                      </li>
                    )
                )}
              </ul>
            </div>

            <div className={styles.section}>
              <h3>מנוע ומערכות</h3>
              <ul>
                {[
                  "sug_delek_nm",
                  "nefah_manoa",
                  "nefach_manoa",
                  "degem_manoa",
                  "mispar_manoa",
                  "hanaa_nm",
                  "delek_nm",
                  "technologiat_hanaa_nm",
                  "koah_sus",
                  "mazgan_ind",
                  "abs_ind",
                  "automatic_ind",
                ].map(
                  (field) =>
                    stableData[field] && (
                      <li key={field}>
                        <p>{fieldNames[field]}:</p>
                        <p>
                          {field === "automatic_ind"
                            ? getGearboxType(stableData[field])
                            : removeLeadingZeros(stableData[field])}
                        </p>
                      </li>
                    )
                )}
              </ul>
            </div>

            <div className={styles.section}>
              <h3>מפרט טכני</h3>
              {Number(licenseData?.kvutzat_zihum) > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 0 12px 0",
                    gap: 8,
                  }}
                >
                  <span style={{ color: "#333" }}>קבוצת זיהום</span>
                  <div
                    style={{
                      display: "flex",
                      gap: 2,
                      direction: "ltr",
                      width: "100%",
                    }}
                  >
                    {(() => {
                      const active = Number(licenseData?.kvutzat_zihum);
                      const max = 15;
                      const items = Array.from(
                        { length: max },
                        (_, i) => max - i
                      );
                      const colorMap = {
                        1: "#005600",
                        2: "#006401",
                        3: "#008000",
                        4: "#049006",
                        5: "#18a020",
                        6: "#3eaf23",
                        7: "#97cb00",
                        8: "#f7d128",
                        9: "#ffbd3e",
                        10: "#ff9934",
                        11: "#e6721f",
                        12: "#d4560a",
                        13: "#db1d20",
                        14: "#c70d0c",
                        15: "#a70302",
                      };
                      return items.map((val) => (
                        <div
                          key={val}
                          style={{
                            width: "auto",
                            height: active === val ? 22 : 20,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "70%",
                            fontWeight: 700,
                            background: colorMap[val],
                            color: "#ffffff",
                            flex: "1 1 0%",
                            border:
                              active === val
                                ? "1px solid rgba(0,0,0,0.5)"
                                : "1px solid transparent",
                            boxShadow:
                              active === val
                                ? "0 0 0 1px rgba(255,255,255,0.6) inset, 0 1px 2px rgba(0,0,0,0.35)"
                                : "none",
                            outline:
                              active === val
                                ? "1px solid rgba(0,0,0,0.75)"
                                : "none",
                          }}
                          title={`קבוצת זיהום ${val}`}
                        >
                          {val}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
              <ul>
                {[
                  "mispar_dlatot",
                  "mispar_moshavim",
                  "mispar_mekomot",
                  "mispar_mekomot_leyd_nahag",
                  "mishkal_kolel",
                  "mishkal_azmi",
                  "mishkal_mitan_harama",
                  "ramat_gimur",
                  "ramat_eivzur_betihuty",
                  "zmig_kidmi",
                  "zmig_ahori",
                  "kosher_grira_bli_blamim",
                  "kosher_grira_im_blamim",
                  "grira_nm",
                ].map(
                  (field) =>
                    stableData[field] && (
                      <li key={field}>
                        <p>{fieldNames[field]}:</p>
                        <p>{removeLeadingZeros(stableData[field])}</p>
                      </li>
                    )
                )}
              </ul>
            </div>

            <div className={styles.section}>
              <h3>מידע נוסף ותאריכים</h3>
              <ul>
                {[
                  "tokef_dt",
                  "mivchan_acharon_dt",
                  "moed_aliya_lakvish",
                  "horaat_rishum",
                  "kvuzat_agra_cd",
                  "degem_cd",
                  "tozeret_cd",
                  "sug_degem",
                ].map(
                  (field) =>
                    stableData[field] && (
                      <li key={field}>
                        <p>{fieldNames[field]}:</p>
                        <p>{stableData[field]}</p>
                      </li>
                    )
                )}
              </ul>
            </div>

            <div className={styles.section}>
              <h3>אביזרי בטיחות</h3>
              <div
                style={{
                  padding: 8,
                  background: "#fafafa",
                  borderRadius: 8,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 12,
                    gap: 8,
                  }}
                >
                  <span style={{ color: "#333" }}>ניקוד בטיחותי</span>
                  <div style={{ display: "flex", gap: 6, direction: "ltr" }}>
                    {(() => {
                      let active = Math.floor(
                        Number(licenseData?.nikud_betihut)
                      );
                      if (!Number.isFinite(active)) {
                        active = null;
                      } else {
                        if (active < 0) active = 0;
                        if (active > 8) active = 8;
                      }
                      const colors = [
                        "#b91c1c", // 0 - red
                        "#f97316", // 1 - orange
                        "#f59e0b", // 2 - amber
                        "#facc15", // 3 - yellow
                        "#22c55e", // 4 - green
                        "#14b8a6", // 5 - teal/greenish
                        "#0ea5e9", // 6 - light blue (sky)
                        "#2563eb", // 7 - blue
                        "#1d4ed8", // 8 - deep blue
                      ];
                      const order = [8, 7, 6, 5, 4, 3, 2, 1, 0];
                      return order.map((val) => (
                        <div
                          key={val}
                          style={{
                            width: 28,
                            height: active === val ? 28 : 26,
                            borderRadius: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            background: colors[val],
                            color: "#ffffff",
                            border:
                              active === val
                                ? "1px solid rgba(0,0,0,0.9)"
                                : "1px solid transparent",
                            boxShadow:
                              active === val
                                ? "0 0 0 2px rgba(255,255,255,0.6) inset, 0 1px 2px rgba(0,0,0,0.35)"
                                : "none",
                            outline:
                              active === val
                                ? "1px solid rgba(0,0,0,0.75)"
                                : "none",
                            outlineOffset: 0,
                          }}
                          title={`ניקוד ${val}`}
                        >
                          {val}
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ color: "#333" }}>סוג תקינה</span>
                  <span style={{ fontWeight: 600 }}>
                    {licenseData?.sug_tkina_nm || "-"}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ color: "#333" }}>מספר כריות אוויר</span>
                  <span style={{ fontWeight: 600 }}>
                    {licenseData?.mispar_kariot_avir ?? "-"}
                  </span>
                </div>
              </div>

              <ul>
                {[
                  { label: "בקרת יציבות", field: "bakarat_yatzivut_ind" },
                  { label: "בקרת סטייה", field: "bakarat_stiya_menativ_ind" },
                  {
                    label: "ניטור מרחק מלפנים",
                    field: "nitur_merhak_milfanim_ind",
                  },
                  {
                    label: "בקרת שיוט אדפטיבית",
                    field: "bakarat_shyut_adaptivit_ind",
                  },
                  {
                    label: "זיהוי בשטח נסתר",
                    field: "zihuy_beshetah_nistar_ind",
                  },
                  {
                    label: "זיהוי הולכי רגל",
                    field: "zihuy_holchey_regel_ind",
                  },
                  {
                    label: "מערכת עזר לבלמים",
                    field: "maarechet_ezer_labalam_ind",
                  },
                  { label: "מצלמת רוורס", field: "matzlemat_reverse_ind" },
                  {
                    label: "חיישני לחץ אוויר",
                    field: "hayshaney_lahatz_avir_batzmigim_ind",
                  },
                  {
                    label: "חיישני חגורות בטיחות",
                    field: "hayshaney_hagorot_ind",
                  },
                  { label: "מערכת ABS", field: "abs_ind" },
                  {
                    label: "תאורה אוטומטית בנסיעה קדימה",
                    field: "teura_automatit_benesiya_kadima_ind",
                  },
                  {
                    label: "שליטה אוטומטית באורות גבוהים",
                    field: "shlita_automatit_beorot_gvohim_ind",
                  },
                  {
                    label: "בלימה אוטומטית בהתקרבות מסוכנת",
                    field: "zihuy_matzav_hitkarvut_mesukenet_ind",
                  },
                  {
                    label: "זיהוי תמרורי תנועה",
                    field: "zihuy_tamrurey_tnua_ind",
                  },
                ].map(({ label, field }) => (
                  <li key={field}>
                    <p>{label}</p>
                    <p
                      style={{
                        color:
                          Number(licenseData?.[field]) === 1
                            ? "#16a34a"
                            : "#dc2626",
                        fontWeight: 700,
                      }}
                    >
                      {Number(licenseData?.[field]) === 1 ? "✓" : "✗"}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            {(isHistoryLoading || historyData) && (
              <div className={styles.section}>
                <h3>רישום והיסטוריה</h3>
                <ul>
                  {isHistoryLoading && (
                    <li>
                      <p>נתונים:</p>
                      <p>בודק...</p>
                    </li>
                  )}
                  {!isHistoryLoading && historyData && (
                    <>
                      {[
                        "mispar_rechev",
                        "mispar_manoa",
                        "kilometer_test_aharon",
                      ].map((field) =>
                        historyData[field] ? (
                          <li key={field}>
                            <p>{historyFieldNames[field]}:</p>
                            <p>{historyData[field]}</p>
                          </li>
                        ) : null
                      )}
                      {[
                        "gapam_ind",
                        "shnui_zeva_ind",
                        "shinui_zmig_ind",
                        "shinui_mivne_ind",
                      ].map((field) =>
                        historyData[field] !== undefined &&
                        historyData[field] !== null ? (
                          <li key={field}>
                            <p>{historyFieldNames[field]}:</p>
                            <p>{toYesNo(historyData[field])}</p>
                          </li>
                        ) : null
                      )}
                      {historyData["rishum_rishon_dt"] && (
                        <li>
                          <p>{historyFieldNames["rishum_rishon_dt"]}:</p>
                          <p>{formatDate(historyData["rishum_rishon_dt"])}</p>
                        </li>
                      )}
                      {historyData["mkoriut_nm"] !== undefined && (
                        <li>
                          <p>{historyFieldNames["mkoriut_nm"]}:</p>
                          <p>{historyData["mkoriut_nm"] || ""}</p>
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </div>
            )}

            {showRecallDetails &&
              Array.isArray(recalls) &&
              recalls.length > 0 && (
                <div className={styles.section}>
                  <h3>קריאות ריקול פתוחות</h3>
                  <ul>
                    {recalls.map((r) => (
                      <li key={r["RECALL_ID"]}>
                        <p>
                          {recallFieldNames["RECALL_ID"]}: {r["RECALL_ID"]}
                        </p>
                        <p>
                          {recallFieldNames["SUG_RECALL"]}: {r["SUG_RECALL"]}
                        </p>
                        <p>
                          {recallFieldNames["SUG_TAKALA"]}: {r["SUG_TAKALA"]}
                        </p>
                        <p>
                          {recallFieldNames["TEUR_TAKALA"]}: {r["TEUR_TAKALA"]}
                        </p>
                        <p>
                          {recallFieldNames["TAARICH_PTICHA"]}:{" "}
                          {formatDate(r["TAARICH_PTICHA"])}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {(() => {
              const fee = calcLicenseFee(null, stableData);
              return (
                fee && (
                  <div className={styles.section}>
                    <h3>אגרת רישוי שנתית</h3>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 8,
                        padding: "10px 12px",
                        background: "#fafafa",
                        borderRadius: 8,
                      }}
                    >
                      <span style={{ fontWeight: 700, fontSize: 18 }}>
                        {formatFee(fee.amount)}
                      </span>
                      <span style={{ color: "#666", fontSize: 13 }}>
                        {fee.note}
                      </span>
                      <a
                        href="https://www.gov.il/he/pages/drivers-car-license-fee-boards"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: 13,
                          color: "#1c58da",
                          textDecoration: "underline",
                        }}
                      >
                        איך חישבנו? לוחות אגרות רישוי — משרד התחבורה
                      </a>
                    </div>
                  </div>
                )
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseModal;
